/**
 * Indice que avanza solo cada tanto, para el carrusel y los bloques de
 * promociones de la portada.
 *
 * Avanza solo, pero se detiene cuando corresponde detenerse (WCAG 2.2.2):
 * - con el puntero encima, para que no se mueva bajo el dedo de quien esta
 *   leyendo;
 * - con el foco del TECLADO adentro (ver `alEnfocar`);
 * - si la pestana no se ve: seguir contando en segundo plano no muestra nada y
 *   al volver la lamina habria saltado sin transicion;
 * - si la persona lo detuvo con el boton (`detenida`).
 *
 * **Ya no se detiene con `prefers-reduced-motion` (16-09-2026).** Hasta ese dia
 * si, y el resultado fue un carrusel que no avanzaba ni se deslizaba en los
 * equipos con los efectos de animacion de Windows apagados —que son muchos, y
 * entre ellos los de quien lo reviso—, con un boton de pausa que no pausaba
 * nada. Lo que esa preferencia pide es menos DESPLAZAMIENTO, no que el
 * contenido deje de cambiar: el CSS la atiende cambiando el deslizamiento por
 * un fundido (`carrusel.css`), y el boton de pausa sigue siendo la forma de
 * detenerlo, que es lo que exige WCAG 2.2.2.
 *
 * Mover a mano reinicia la cuenta: si alguien aprieta "siguiente" a medio
 * intervalo, la lamina nueva se queda el tiempo completo y no un resto.
 *
 * En el telefono no hay flechas ni puntos (`carrusel.css`): se desliza con el
 * dedo. Y el "puntero encima" es solo el del mouse: un toque dispara un
 * `mouseenter` emulado sin su `mouseleave`, y el carrusel quedaba detenido.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FocusEvent, PointerEvent, TouchEvent } from 'react';

/** Cuanto hay que deslizar el dedo, en pixeles, para que cuente como pasar de lamina. */
const DESLIZAMIENTO_MINIMO = 40;

export interface Rotacion {
  actual: number;
  /** Salta a una lamina concreta. */
  ir(indice: number): void;
  /** Avanza o retrocede; el ultimo enlaza con el primero. */
  mover(salto: number): void;
  /** Los manejadores que van en el contenedor para que el avance se detenga. */
  manejadores: {
    onPointerEnter(evento: PointerEvent<HTMLElement>): void;
    onPointerLeave(evento: PointerEvent<HTMLElement>): void;
    onTouchStart(evento: TouchEvent<HTMLElement>): void;
    onTouchEnd(evento: TouchEvent<HTMLElement>): void;
    onFocus(evento: FocusEvent<HTMLElement>): void;
    onBlur(evento: FocusEvent<HTMLElement>): void;
  };
}

export function useRotacion(cantidad: number, intervaloMs: number, detenida = false): Rotacion {
  const [actual, setActual] = useState(0);
  // Cambia con cada movimiento manual y reinicia el intervalo.
  const [ciclo, setCiclo] = useState(0);
  const [visible, setVisible] = useState(true);
  // Dos razones distintas para pausar, y cada una se levanta sola: con un solo
  // indicador, sacar el puntero reanudaba aunque el foco del teclado siguiera
  // adentro, y al reves.
  const [bajoPuntero, setBajoPuntero] = useState(false);
  const [conFocoDeTeclado, setConFocoDeTeclado] = useState(false);
  const pausada = bajoPuntero || conFocoDeTeclado;

  // Si la lista se achica (un banner apagado desde el panel), el indice no
  // puede quedar apuntando a una lamina que ya no existe.
  useEffect(() => {
    if (actual >= cantidad) setActual(0);
  }, [actual, cantidad]);

  useEffect(() => {
    const alCambiar = (): void => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', alCambiar);
    return () => document.removeEventListener('visibilitychange', alCambiar);
  }, []);

  useEffect(() => {
    if (cantidad <= 1 || detenida || pausada || !visible) return;

    const temporizador = window.setInterval(() => {
      setActual((indice) => (indice + 1) % cantidad);
    }, intervaloMs);
    return () => window.clearInterval(temporizador);
  }, [cantidad, intervaloMs, detenida, pausada, visible, ciclo]);

  const ir = useCallback((indice: number): void => {
    setActual(indice);
    setCiclo((valor) => valor + 1);
  }, []);

  const mover = useCallback(
    (salto: number): void => {
      setActual((indice) => (indice + salto + cantidad) % cantidad);
      setCiclo((valor) => valor + 1);
    },
    [cantidad],
  );

  /**
   * El foco pausa solo si llego con el TECLADO (`:focus-visible`). Un clic en
   * una flecha tambien deja el foco en el boton, y con eso el carrusel quedaba
   * detenido despues de usarlo con el mouse, sin que nadie lo hubiera pedido.
   */
  const alEnfocar = useCallback((evento: FocusEvent<HTMLElement>): void => {
    if (evento.target instanceof HTMLElement && evento.target.matches(':focus-visible')) {
      setConFocoDeTeclado(true);
    }
  }, []);

  /** Solo cuenta como salir si el foco se fue del contenedor, no a otro boton de adentro. */
  const alDesenfocar = useCallback((evento: FocusEvent<HTMLElement>): void => {
    if (!evento.currentTarget.contains(evento.relatedTarget)) setConFocoDeTeclado(false);
  }, []);

  /** Donde se apoyo el dedo, para medir el deslizamiento al levantarlo. */
  const inicioToque = useRef<{ x: number; y: number } | null>(null);

  const alTocar = useCallback((evento: TouchEvent<HTMLElement>): void => {
    const toque = evento.touches[0];
    inicioToque.current = toque !== undefined ? { x: toque.clientX, y: toque.clientY } : null;
  }, []);

  /** Solo cuenta si fue mas de lado que de alto: si no, era bajar la pagina. */
  const alSoltar = useCallback(
    (evento: TouchEvent<HTMLElement>): void => {
      const inicio = inicioToque.current;
      const toque = evento.changedTouches[0];
      inicioToque.current = null;
      if (inicio === null || toque === undefined || cantidad <= 1) return;

      const dx = toque.clientX - inicio.x;
      const dy = toque.clientY - inicio.y;
      if (Math.abs(dx) < DESLIZAMIENTO_MINIMO || Math.abs(dx) <= Math.abs(dy)) return;
      mover(dx < 0 ? 1 : -1);
    },
    [cantidad, mover],
  );

  const manejadores = {
    onPointerEnter: (evento: PointerEvent<HTMLElement>) => {
      if (evento.pointerType === 'mouse') setBajoPuntero(true);
    },
    onPointerLeave: (evento: PointerEvent<HTMLElement>) => {
      if (evento.pointerType === 'mouse') setBajoPuntero(false);
    },
    onTouchStart: alTocar,
    onTouchEnd: alSoltar,
    onFocus: alEnfocar,
    onBlur: alDesenfocar,
  };

  return { actual, ir, mover, manejadores };
}
